//added for quality flag
import { useMemo } from 'react';

const hasQualityErrors = (quality) => {
  return Boolean(quality?.hasErrors || quality?.has_errors);
};

const normalizeErrors = (quality, image) => {
  if (!quality || !Array.isArray(quality.errors)) return [];

  const imageId = quality.imageId || image?.id;
  return quality.errors.map((err) => ({
    imageId,
    instanceNumber: image?.instanceNumber || image?.imageNumber,
    class: err.class || err.className,
    message: err.message
  }));
};

const useImageQuality = (studyData) => {
  const qualityData = useMemo(() => {
    if (!studyData) return { studyErrors: [], seriesErrorsMap: {}, imageErrorsMap: {} };

    const studyErrors = [];
    const seriesErrorsMap = {};
    const imageErrorsMap = {};

    // 1. Get the list of studies to process
    // Handle cases where the whole API response { result, message, data } is passed
    const baseData = studyData?.data || studyData;

    const studies = baseData?.studyDtoList?.length > 0
      ? baseData.studyDtoList
      : [baseData];

    // 2. Aggregate ALL image quality data from ALL studies into one array
    const allQualityEntries = [];
    studies.forEach(study => {
      const q = study?.imageQuality || study?.image_quality;
      if (Array.isArray(q)) {
        allQualityEntries.push(...q);
      }
    });

    // 3. Build the qualityMap from the aggregated entries
    const qualityMap = allQualityEntries.reduce((acc, q) => {
      const id = q?.imageId || q?.image_id;
      if (id) acc[String(id)] = q;
      return acc;
    }, {});

    studies.forEach((study) => {
      const seriesList = study?.seriesList;
      if (!seriesList) return;

      seriesList.forEach((series) => {
        const modality = series.modality?.name?.toUpperCase() || (typeof series.modality === 'string' ? series.modality.toUpperCase() : '');
        const desc = series?.seriesDescription?.toLowerCase() ?? '';
        const isSecondaryCapture = desc === 'ai reference' || desc === 'secondary capture';

        if (modality === 'SR' || modality === 'OT' || modality === 'DOC' || isSecondaryCapture) {
          return;
        }

        const seriesErrors = [];
        series.imageList?.forEach((image) => {
          const quality = qualityMap[String(image.id)] || image.imageQuality || image.image_quality;

          if (hasQualityErrors(quality)) {
            const imageErrors = normalizeErrors(quality, image);
            imageErrorsMap[String(image.id)] = imageErrors;
            if (image.sopInstanceUID) {
              imageErrorsMap[String(image.sopInstanceUID)] = imageErrors;
            }
            seriesErrors.push(...imageErrors);
            studyErrors.push(...imageErrors);
          }
        });

        if (seriesErrors.length > 0) {
          console.log(`Mapping series errors to ID: ${series.id} and UID: ${series.seriesInstanceUid}`);
          seriesErrorsMap[String(series.id)] = seriesErrors;
          if (series.seriesInstanceUid) {
            seriesErrorsMap[String(series.seriesInstanceUid)] = seriesErrors;
          }
        }
      });
    });

    return { studyErrors, seriesErrorsMap, imageErrorsMap };
  }, [studyData]);

  return qualityData;
};


export default useImageQuality;
